import { Fragment, useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box, Card, CardContent, Typography, Grid, Chip, CircularProgress, Alert, AlertTitle,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip,
  Divider, TextField, Button, Link, Snackbar,
} from '@mui/material'
import { Refresh, Warning, OpenInNew, Edit, Close } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import {
  analysisApi, reachApi, ReachCost, ReachCostComponent, ReachPlatformCosts,
} from '../services/api'
import { RootState } from '../store'
import useCanContactVendor from '../hooks/useCanContactVendor'

interface CostRange { low: number; high: number }

interface RepoRollupEntry {
  repo_identity_hash: string
  repository_name: string
  analysis_id: string
  cost_per_request_usd: CostRange | null
  cost_per_day_usd: CostRange | null
  entry_point_agents_total: number
  non_entry_point_agents_excluded: number
}

interface CostRollup {
  cost_estimation_visible: boolean
  repos_total: number
  repos_priced: number
  repos_with_daily_estimate: number
  cost_per_request_usd: CostRange | null
  cost_per_day_usd: CostRange | null
  repos: RepoRollupEntry[]
}

interface TopAgent {
  repo_identity_hash: string
  repo_name: string
  analysis_id: string
  agent_id_hint: string
  agent_name: string
  architecture_pattern: string | null
  llm_model: string | null
  provider: string | null
  cost_per_request_usd: CostRange | null
  cost_per_day_usd: CostRange | null
  is_unbounded_risk: boolean
}

interface ModelPriced {
  llm_model: string
  provider: string
  agents_count: number
  cost_per_request_usd: CostRange | null
  cost_per_day_usd: CostRange | null
  // False when this figure comes from a provider-level fallback band rather
  // than an exact pricing-catalog match (e.g. a model recently deprecated
  // upstream but whose provider is still inferable) — still a real $
  // estimate, just a rougher one.
  pricing_matched: boolean
}

interface ModelUnpriced {
  llm_model: string
  provider: string
  agents_count: number
  tokens_per_request: CostRange
}

interface CostRanking {
  cost_estimation_visible: boolean
  top_agents: TopAgent[]
  agents_ranked_total: number
  by_model: {
    vendor_metered: ModelPriced[]
    self_hosted: ModelUnpriced[]
    unmatched: ModelUnpriced[]
  }
  unbounded_risk_agents: TopAgent[]
}

const fmtCost = (v: number) => (v < 0.01 ? v.toFixed(4) : v.toFixed(2))
const fmtRange = (r: CostRange | null) => (r ? `$${fmtCost(r.low)}–$${fmtCost(r.high)}` : '—')

const CostAnalysisPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const [searchParams] = useSearchParams()
  // Admins land here from AnalyticsPage's cross-tenant lookup with an
  // explicit ?tenant_id= override; tenant users always see their own.
  const tenantId = searchParams.get('tenant_id') || user?.tenant_id
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [visible, setVisible] = useState(false)
  const [rollup, setRollup] = useState<CostRollup | null>(null)
  const [ranking, setRanking] = useState<CostRanking | null>(null)
  // Agent Reach is a separate enterprise entitlement, so this stays null for
  // most tenants and the section below simply does not render. A 404 here is
  // the ordinary case, not an error worth showing anyone.
  const [reachCost, setReachCost] = useState<ReachCost | null>(null)
  const [platformCost, setPlatformCost] = useState<ReachPlatformCosts | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [draftCost, setDraftCost] = useState('')
  const [draftBasis, setDraftBasis] = useState('')
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const canPrice = user?.role === 'admin' || user?.role === 'manager'
  // Separate from canPrice on purpose. Editing a price is an operational
  // permission a manager holds; asking ARIAS to change what the contract
  // grants is not, and conflating them would tell managers to email sales
  // about something only an admin can agree to.
  const canContactVendor = useCanContactVendor()

  useEffect(() => {
    if (tenantId) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])

  const load = async () => {
    if (!tenantId) return
    setLoading(true)
    try {
      const [rollupResp, rankingResp, reachResp, platformResp] = await Promise.allSettled([
        analysisApi.getTenantCostRollup(tenantId),
        analysisApi.getTenantCostRanking(tenantId),
        reachApi.cost(),
        reachApi.platformCost(),
      ])
      const rollupData = rollupResp.status === 'fulfilled' ? rollupResp.value.data as CostRollup : null
      const rankingData = rankingResp.status === 'fulfilled' ? rankingResp.value.data as CostRanking : null
      setRollup(rollupData)
      setRanking(rankingData)
      setReachCost(reachResp.status === 'fulfilled' ? reachResp.value.data : null)
      setPlatformCost(platformResp.status === 'fulfilled' ? platformResp.value.data : null)
      setVisible(!!rollupData?.cost_estimation_visible)
    } finally {
      setLoading(false)
    }
  }

  const goToAgent = (a: TopAgent) => navigate(`/analyses/${a.analysis_id}#agent-${a.agent_id_hint}`)

  const openEditor = (c: ReachCostComponent) => {
    setEditing(c.identity)
    setDraftCost(c.monthly_cost_usd === null ? '' : String(c.monthly_cost_usd))
    setDraftBasis(c.cost_basis || '')
  }

  const saveCost = async (identity: string) => {
    const amount = Number(draftCost)
    if (draftCost.trim() === '' || Number.isNaN(amount) || amount < 0) {
      setNotice('Enter a monthly figure of zero or more.')
      return
    }
    setSaving(true)
    try {
      await reachApi.setCost(identity, amount, draftBasis.trim() || undefined)
      setEditing(null)
      const [components, platforms] = await Promise.all([
        reachApi.cost(), reachApi.platformCost(),
      ])
      setReachCost(components.data)
      setPlatformCost(platforms.data)
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Could not save that figure.')
    } finally {
      setSaving(false)
    }
  }

  const removeCost = async (identity: string) => {
    setSaving(true)
    try {
      await reachApi.clearCost(identity)
      setEditing(null)
      const [components, platforms] = await Promise.all([
        reachApi.cost(), reachApi.platformCost(),
      ])
      setReachCost(components.data)
      setPlatformCost(platforms.data)
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || 'Could not withdraw that figure.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!visible) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h4" gutterBottom>Cost Analysis</Typography>
        <Alert severity="info">
          <AlertTitle>Not available</AlertTitle>
          Cost &amp; token estimation is included on higher plan tiers.{' '}
          {canContactVendor
            ? 'Contact sales@tryarias.com to enable it.'
            : 'Ask your tenant admin to enable it.'}
        </Alert>
      </Box>
    )
  }

  const priced = rollup?.repos_priced ?? 0
  const total = rollup?.repos_total ?? 0
  const dayEstimated = rollup?.repos_with_daily_estimate ?? 0

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h4">Cost Analysis</Typography>
          <Typography variant="body2" color="text.secondary">
            Projected LLM spend across your scanned repos — which agents, which models, and which are structurally unbounded.
          </Typography>
        </Box>
        <IconButton onClick={load} aria-label="Refresh"><Refresh /></IconButton>
      </Box>

      {/* Header summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Projected cost / request</Typography>
              <Typography variant="h5">{fmtRange(rollup?.cost_per_request_usd ?? null)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {priced} of {total} repo{total === 1 ? '' : 's'} priced
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Projected cost / day</Typography>
              <Typography variant="h5">{fmtRange(rollup?.cost_per_day_usd ?? null)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {dayEstimated} of {priced} priced repo{priced === 1 ? '' : 's'} have a volume estimate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Most expensive repos */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Most expensive repos</Typography>
              {!rollup?.repos.length ? (
                <Typography variant="body2" color="text.secondary">No priced repos yet.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Repo</TableCell>
                        <TableCell align="right">Cost / req</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rollup.repos.filter(r => r.cost_per_request_usd).map(r => (
                        <TableRow key={r.repo_identity_hash} hover>
                          <TableCell>
                            <Typography variant="body2">{r.repository_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {r.entry_point_agents_total} independent agent{r.entry_point_agents_total === 1 ? '' : 's'}
                              {r.non_entry_point_agents_excluded > 0 && `, ${r.non_entry_point_agents_excluded} delegate(s) excluded`}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtRange(r.cost_per_request_usd)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Open scan">
                              <IconButton size="small" onClick={() => navigate(`/analyses/${r.analysis_id}`)}>
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Most expensive agents */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Most expensive agents</Typography>
              {!ranking?.top_agents.length ? (
                <Typography variant="body2" color="text.secondary">No priced agents yet.</Typography>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Agent</TableCell>
                          <TableCell align="right">Cost / req</TableCell>
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ranking.top_agents.map(a => (
                          <TableRow key={`${a.analysis_id}-${a.agent_id_hint}`} hover>
                            <TableCell>
                              <Typography variant="body2">{a.agent_name || a.agent_id_hint}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.repo_name}{a.is_unbounded_risk && ' · unbounded cost risk'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{fmtRange(a.cost_per_request_usd)}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="View agent detail">
                                <IconButton size="small" onClick={() => goToAgent(a)}>
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {ranking.agents_ranked_total > ranking.top_agents.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Showing top {ranking.top_agents.length} of {ranking.agents_ranked_total} priced agents.
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Most expensive models */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Most expensive models</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Ranked by total projected spend across every agent using that model — not the model's per-token price. A cheaper model used by many agents can outrank a pricier one used once.
              </Typography>
              {!ranking?.by_model.vendor_metered.length ? (
                <Typography variant="body2" color="text.secondary">No priced models yet.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Model</TableCell>
                        <TableCell align="right">Agents</TableCell>
                        <TableCell align="right">Total cost / req</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ranking.by_model.vendor_metered.map(m => (
                        <TableRow key={`${m.llm_model}-${m.provider}`} hover>
                          <TableCell>
                            <Typography variant="body2">{m.llm_model}</Typography>
                            <Chip label={m.provider} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                          </TableCell>
                          <TableCell align="right">{m.agents_count}</TableCell>
                          <TableCell align="right">
                            {m.pricing_matched ? (
                              fmtRange(m.cost_per_request_usd)
                            ) : (
                              <Tooltip title="Estimated from provider-level pricing, not an exact catalog match for this model — still a real estimate, just a rougher one.">
                                <span style={{ cursor: 'help', borderBottom: '1px dotted' }}>
                                  {fmtRange(m.cost_per_request_usd)}
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {(!!ranking?.by_model.self_hosted.length || !!ranking?.by_model.unmatched.length) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Not vendor-priced — never shown as $0
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {ranking!.by_model.self_hosted.map(m => (
                      <Tooltip key={`sh-${m.llm_model}`} title="Self-hosted / local model — infra cost (e.g. GPU-hours), not vendor-metered per token. Not priced today.">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Self-hosted" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                          <Typography variant="body2">{m.llm_model}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.agents_count} agent{m.agents_count === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))}
                    {ranking!.by_model.unmatched.map(m => (
                      <Tooltip key={`um-${m.llm_model}`} title="No pricing catalog match and no known provider — a coverage gap, not a deliberately unbilled model.">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Unresolved" size="small" color="default" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                          <Typography variant="body2">{m.llm_model}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.agents_count} agent{m.agents_count === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Unbounded cost risk */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderLeft: 4, borderColor: 'warning.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Warning color="warning" fontSize="small" />
                <Typography variant="h6">Unbounded cost risk</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Agents with unbounded conversation memory and no real iteration cap — each turn resends the entire growing history, so cost can grow far beyond the estimate above if left unchecked.
              </Typography>
              {!ranking?.unbounded_risk_agents.length ? (
                <Typography variant="body2" color="text.secondary">No flagged agents.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {ranking.unbounded_risk_agents.map(a => (
                    <Box key={`${a.analysis_id}-${a.agent_id_hint}`}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box>
                        <Typography variant="body2">{a.agent_name || a.agent_id_hint}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.repo_name}</Typography>
                      </Box>
                      <Tooltip title="View agent detail">
                        <IconButton size="small" onClick={() => goToAgent(a)}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/*
        Shared infrastructure — the Agent Reach contribution.

        Deliberately below its own divider rather than folded into the cards
        above, because the money changes meaning here. Everything above is
        ARIAS estimating LLM spend from what your repositories declare.
        Everything below is a figure you supplied about infrastructure we can
        see but cannot price. Adding the two would produce a total that mixes
        an estimate with an assumption, and nobody could defend it in a budget
        conversation.

        What reach contributes is the denominator. A store costing a known
        amount, reached by eleven agents across four repositories, is a
        per-agent figure nobody could produce before, because nobody could see
        all four repositories at once.
      */}
      {reachCost && (
        <>
          <Divider sx={{ my: 4 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5">Shared infrastructure</Typography>
            <Typography variant="body2" color="text.secondary">
              Vector stores, knowledge bases and search indexes your agents reach — including the ones declared in a different repository from the agents using them.
            </Typography>
          </Box>

          {!reachCost.components.length ? (
            <Alert severity="info">
              No shared components resolved yet. They appear once a scanned repository declares one and a scanned agent reaches it.
            </Alert>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Attributed / month</Typography>
                      <Typography variant="h5">
                        ${reachCost.totals.monthly_attributed_usd.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Across {reachCost.totals.components_priced} component{reachCost.totals.components_priced === 1 ? '' : 's'} you have priced
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Not yet priced</Typography>
                      <Typography variant="h5">{reachCost.totals.components_unpriced}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Counted, never assumed to be free
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {reachCost.totals.orphaned > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ borderLeft: 4, borderColor: 'warning.main' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Nothing reaches them</Typography>
                        <Typography variant="h5">{reachCost.totals.orphaned}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Genuinely unused, or reached from a repository nobody has scanned
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Component</TableCell>
                      <TableCell>Declared in</TableCell>
                      <TableCell align="right">Agents reaching it</TableCell>
                      <TableCell align="right">Monthly</TableCell>
                      <TableCell align="right">Per agent</TableCell>
                      {canPrice && <TableCell align="right" />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reachCost.components.map(c => (
                      <Fragment key={c.identity}>
                        <TableRow hover>
                          <TableCell>
                            <Typography variant="body2">{c.identity}</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                              <Chip label={c.component_kind.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              {Object.entries(c.cost_drivers).map(([k, v]) => (
                                <Tooltip key={k} title="Declared in your infrastructure, and one of the things that drives what this costs.">
                                  <Chip label={`${k.replace(/_/g, ' ')}: ${v}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                </Tooltip>
                              ))}
                              {c.orphaned && (
                                <Tooltip title="No scanned agent reaches this. Either it is unused, or the repository that uses it has not been scanned.">
                                  <Chip label="unreached" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {c.declared_in.length ? c.declared_in.join(', ') : 'No scanned repository declares it'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {c.dependent_count === 0 ? (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            ) : (
                              <Tooltip title={c.dependent_agents.join(', ')}>
                                <Link component="button" variant="body2" underline="hover"
                                  onClick={() => navigate(`/reach?tab=components&identity=${encodeURIComponent(c.identity)}`)}>
                                  {c.dependent_count}
                                </Link>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {c.monthly_cost_usd === null ? (
                              <Typography variant="body2" color="text.secondary">Not stated</Typography>
                            ) : (
                              <Tooltip title={c.cost_basis || 'No basis recorded for this figure.'}>
                                <span style={{ cursor: 'help' }}>${c.monthly_cost_usd.toFixed(2)}</span>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {c.per_agent_monthly_usd === null ? (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            ) : (
                              <Tooltip title={`Split ${c.split} across ${c.dependent_count} agent${c.dependent_count === 1 ? '' : 's'}. An even split, because weighting it would need request volumes we cannot see.`}>
                                <span style={{ cursor: 'help' }}>${c.per_agent_monthly_usd.toFixed(2)}</span>
                              </Tooltip>
                            )}
                          </TableCell>
                          {canPrice && (
                            <TableCell align="right">
                              <Tooltip title={c.monthly_cost_usd === null ? 'State what this costs' : 'Change this figure'}>
                                <IconButton size="small" onClick={() => openEditor(c)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                        {editing === c.identity && (
                          <TableRow>
                            <TableCell colSpan={canPrice ? 6 : 5} sx={{ bgcolor: 'action.hover' }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', py: 1 }}>
                                <TextField
                                  label="Monthly cost (USD)" size="small" type="number" value={draftCost}
                                  onChange={e => setDraftCost(e.target.value)}
                                  inputProps={{ min: 0, step: '0.01' }} sx={{ width: 180 }}
                                />
                                <TextField
                                  label="Where the number came from" size="small" value={draftBasis}
                                  onChange={e => setDraftBasis(e.target.value)}
                                  placeholder="e.g. AWS bill, July — Bedrock KB line item"
                                  helperText="Optional, and worth filling in. A figure nobody can source gets argued with later."
                                  sx={{ flexGrow: 1, minWidth: 260 }}
                                />
                                <Button variant="contained" size="small" disabled={saving}
                                  onClick={() => saveCost(c.identity)}>Save</Button>
                                {c.monthly_cost_usd !== null && (
                                  <Button size="small" color="inherit" disabled={saving}
                                    onClick={() => removeCost(c.identity)}>Withdraw</Button>
                                )}
                                <Button size="small" color="inherit" disabled={saving}
                                  onClick={() => setEditing(null)}>Cancel</Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* The service's own words, rendered rather than restated, so the
                  caveat cannot drift from what the figures actually are. */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {reachCost.basis}
              </Typography>
            </>
          )}
        </>
      )}


      {/*
        Hosting layers — the third kind of money on this page, and the one
        ARIAS most explicitly does not estimate. AgentCore, Agent Engine and
        Foundry are consumption-priced by compute-second, request and region,
        on terms a customer may have negotiated. A figure computed from a
        declaration would be the one number here nobody could source.

        What we contribute is the denominator. Agents on one platform are
        routinely spread across repositories owned by different teams, so
        "this hosting layer carries nineteen agents across five repositories"
        is a sentence no one inside the customer could previously say — and it
        is the sentence that decides whether a platform bill is reasonable.
      */}
      {platformCost && (platformCost.platforms.length > 0 || platformCost.self_hosted_agents > 0) && (
        <>
          <Divider sx={{ my: 4 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5">Hosting layers</Typography>
            <Typography variant="body2" color="text.secondary">
              Where your agents run. Separate from what they cost to call — a managed runtime bills for time and requests, not tokens.
            </Typography>
          </Box>

          {!platformCost.platforms.length ? (
            <Alert severity="info">
              All {platformCost.self_hosted_agents} agent
              {platformCost.self_hosted_agents === 1 ? '' : 's'} run on infrastructure you
              control end to end. No managed hosting layer is referenced in the scanned code.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Platform</TableCell>
                      <TableCell align="right">Agents</TableCell>
                      <TableCell>Repositories</TableCell>
                      <TableCell align="right">Monthly</TableCell>
                      <TableCell align="right">Per agent</TableCell>
                      {canPrice && <TableCell align="right" />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {platformCost.platforms.map(p => (
                      <Fragment key={p.identity}>
                        <TableRow hover>
                          <TableCell>
                            <Typography variant="body2">{p.platform.replace(/_/g, ' ')}</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                              {p.services.map(s => (
                                <Chip key={s} label={s.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              ))}
                              {p.brokers_tool_auth && (
                                <Tooltip title="This platform grants tool access outside the codebase, so permission findings for these agents describe only what the repository shows.">
                                  <Chip label="brokers tool access" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{p.agents}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {p.repositories.join(', ')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {p.monthly_cost_usd === null ? (
                              <Typography variant="body2" color="text.secondary">Not stated</Typography>
                            ) : (
                              <Tooltip title={p.cost_basis || 'No basis recorded for this figure.'}>
                                <span style={{ cursor: 'help' }}>${p.monthly_cost_usd.toFixed(2)}</span>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {p.per_agent_monthly_usd === null
                              ? <Typography variant="body2" color="text.secondary">—</Typography>
                              : `$${p.per_agent_monthly_usd.toFixed(2)}`}
                          </TableCell>
                          {canPrice && (
                            <TableCell align="right">
                              <Tooltip title={p.monthly_cost_usd === null ? 'State what this platform costs' : 'Change this figure'}>
                                <IconButton size="small" onClick={() => openEditor({
                                  identity: p.identity,
                                  monthly_cost_usd: p.monthly_cost_usd,
                                  cost_basis: p.cost_basis,
                                } as ReachCostComponent)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                        {editing === p.identity && (
                          <TableRow>
                            <TableCell colSpan={canPrice ? 6 : 5} sx={{ bgcolor: 'action.hover' }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', py: 1 }}>
                                <TextField
                                  label="Monthly cost (USD)" size="small" type="number" value={draftCost}
                                  onChange={e => setDraftCost(e.target.value)}
                                  inputProps={{ min: 0, step: '0.01' }} sx={{ width: 180 }}
                                />
                                <TextField
                                  label="Where the number came from" size="small" value={draftBasis}
                                  onChange={e => setDraftBasis(e.target.value)}
                                  placeholder="e.g. AWS bill, July — Bedrock AgentCore line item"
                                  sx={{ flexGrow: 1, minWidth: 260 }}
                                />
                                <Button variant="contained" size="small" disabled={saving}
                                  onClick={() => saveCost(p.identity)}>Save</Button>
                                {p.monthly_cost_usd !== null && (
                                  <Button size="small" color="inherit" disabled={saving}
                                    onClick={() => removeCost(p.identity)}>Withdraw</Button>
                                )}
                                <Button size="small" color="inherit" disabled={saving}
                                  onClick={() => setEditing(null)}>Cancel</Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {platformCost.self_hosted_agents > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  A further {platformCost.self_hosted_agents} agent
                  {platformCost.self_hosted_agents === 1 ? '' : 's'} run on infrastructure you
                  control end to end and are not billed by a hosting layer.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {platformCost.basis}
              </Typography>
            </>
          )}
        </>
      )}

      <Snackbar
        open={!!notice} autoHideDuration={6000} onClose={() => setNotice(null)}
        message={notice || ''}
        action={<IconButton size="small" color="inherit" onClick={() => setNotice(null)}><Close fontSize="small" /></IconButton>}
      />
    </Box>
  )
}

export default CostAnalysisPage
